/**
 * The customer waiting for you at their gate.
 *
 * "Take a yard job" used to mean walking to an empty lawn with a floating
 * marker over it. Nobody was there. The brief's priority 8 is the first
 * customer moment: the person who booked you should be standing outside their
 * house, with a name, waiting - and should still be there watching while you
 * work, because being watched is what makes the work feel like a service you
 * are providing rather than a timer you are running down.
 *
 * One figure at a time, built from the same `Humanoid` as the player and the
 * crowd, deterministic per customer name so the same person greets you every
 * time you go back.
 */
import * as THREE from 'three';
import {
  buildHumanoid, HumanoidAnimator,
  SKIN_TONES, HAIR_COLOURS, SHIRT_COLOURS, TROUSER_COLOURS,
} from './Humanoid.js';
import { makeLabel } from './Signposts.js';
import { getLocation } from '../../sim/data/world.js';

export interface CustomerHost {
  locationId: string;
  name: string;
  /** True once the job is under way, so they stand back and watch. */
  working: boolean;
}

export interface CustomerRig {
  group: THREE.Group;
  /** Pass null when there is nobody expecting the player anywhere. */
  update(dt: number, host: CustomerHost | null, playerX: number, playerZ: number): void;
}

/** Beyond this the label is unreadable anyway, so fade the whole thing out. */
const SHOW_DISTANCE = 42;

export function createCustomers(): CustomerRig {
  const group = new THREE.Group();

  const holder = new THREE.Group();
  holder.visible = false;
  group.add(holder);

  // Built once and re-dressed, rather than rebuilt per customer: constructing a
  // humanoid allocates two dozen meshes and this can change every few seconds
  // as the player accepts and finishes jobs.
  const parts = buildHumanoid({
    skin: SKIN_TONES[0]!, hair: HAIR_COLOURS[0]!,
    shirt: SHIRT_COLOURS[0]!, trousers: TROUSER_COLOURS[0]!,
    shoes: 0x2b2b30, height: 1.72,
  });
  holder.add(parts.root);
  const animator = new HumanoidAnimator(parts);

  let label: THREE.Sprite | null = null;
  let shownFor: string | null = null;
  let fade = 0;
  let idle = 0;

  function dress(name: string): void {
    // A stable hash of the name, so Jamal Petrov is the same person every visit.
    let seed = 0;
    for (const ch of name) seed = (seed * 31 + ch.charCodeAt(0)) & 0x7fffffff;
    const pick = <T>(list: T[], salt: number): T =>
      list[Math.abs((seed >> salt) % list.length)]!;

    recolour(parts, {
      skin: pick(SKIN_TONES, 2),
      hair: pick(HAIR_COLOURS, 5),
      shirt: pick(SHIRT_COLOURS, 8),
      trousers: pick(TROUSER_COLOURS, 11),
    });

    if (label) {
      group.remove(label);
      label.material.map?.dispose();
      label.material.dispose();
    }
    label = makeLabel(name, '#ffe9a8');
    // Place labels are read from across the neighbourhood; a person's name is
    // read from a few metres away, so it wants to be about half the size.
    label.scale.multiplyScalar(0.55);
    label.material.opacity = 0;
    group.add(label);
  }

  return {
    group,

    update(dt: number, host: CustomerHost | null, playerX: number, playerZ: number): void {
      if (!host) {
        fade = Math.max(0, fade - dt * 3);
        holder.visible = fade > 0.01;
        if (label) label.material.opacity = fade * 0.95;
        applyFade(parts, fade);
        return;
      }

      if (host.locationId !== shownFor) {
        shownFor = host.locationId;
        dress(host.name);
        const site = getLocation(host.locationId);
        // Beside the front path rather than on it, so they are never something
        // the player has to walk around.
        const outward = Math.hypot(site.entryX, site.entryZ) || 1;
        const ox = site.entryX / outward;
        const oz = site.entryZ / outward;
        const distance = (Math.abs(site.entryX) > Math.abs(site.entryZ)
          ? site.width : site.depth) / 2 + 3.4;
        const x = site.x + ox * distance - oz * 2.6;
        const z = site.z + oz * distance + ox * 2.6;
        holder.position.set(x, 0, z);
        // Facing the street, which is where the player will come from.
        holder.rotation.y = Math.atan2(ox, oz);
        if (label) label.position.set(x, 2.35, z);
      }

      const distance = Math.hypot(playerX - holder.position.x, playerZ - holder.position.z);
      const want = distance < SHOW_DISTANCE ? 1 : 0;
      fade += (want - fade) * Math.min(1, dt * 2.4);
      holder.visible = fade > 0.01;
      applyFade(parts, fade);

      // Turn to watch the player once they are close.
      if (distance < 16) {
        const target = Math.atan2(playerX - holder.position.x, playerZ - holder.position.z);
        const delta = shortest(holder.rotation.y, target);
        holder.rotation.y += delta * Math.min(1, dt * 2.2);
      }

      // Idle, not frozen: a still figure reads as a statue.
      idle += dt;
      animator.update(dt, 0, false, 0, 'locomotion');
      parts.torso.position.y = 0.1 + Math.sin(idle * 1.1) * 0.012;

      if (label) {
        label.position.set(holder.position.x, 2.35, holder.position.z);
        label.material.opacity = fade * 0.95;
      }
    },
  };
}

function shortest(from: number, to: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

/**
 * Re-tint a built humanoid.
 *
 * The figure's materials are shared out of the humanoid material cache, so
 * writing to them would repaint the whole crowd. Each mesh gets its own clone
 * the first time it is recoloured, and after that the clones are reused.
 */
function recolour(
  parts: { root: THREE.Group },
  colours: { skin: number; hair: number; shirt: number; trousers: number },
): void {
  parts.root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const material = mesh.material as THREE.MeshStandardMaterial;
    if (!mesh.userData.role) {
      mesh.userData.role = roleOf(material.color.getHex());
      mesh.material = material.clone();
    }
    const own = mesh.material as THREE.MeshStandardMaterial;
    const role = mesh.userData.role as string;
    if (role === 'skin') own.color.setHex(colours.skin);
    else if (role === 'hair') own.color.setHex(colours.hair);
    else if (role === 'shirt') own.color.setHex(colours.shirt);
    else if (role === 'trousers') own.color.setHex(colours.trousers);
  });
}

/** Which slot a starting colour came from, recorded once at first recolour. */
function roleOf(hex: number): string {
  if (hex === SKIN_TONES[0]) return 'skin';
  if (hex === HAIR_COLOURS[0]) return 'hair';
  if (hex === SHIRT_COLOURS[0]) return 'shirt';
  if (hex === TROUSER_COLOURS[0]) return 'trousers';
  return 'other';
}

function applyFade(parts: { root: THREE.Group }, fade: number): void {
  parts.root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const material = mesh.material as THREE.MeshStandardMaterial;
    material.transparent = fade < 0.99;
    material.opacity = fade;
  });
}
